import { ITransitionData } from '@barba/core/dist/core/src/src/defs';
import { scrollController } from '../../ScrollController';
import { PageEvent } from '../PageEvent';
import { BarbaTransitionVertWipe } from './BarbaTransitionVertWipe';

import barba from '@barba/core';
import barbaPrefetch from '@barba/prefetch';

// declare const barba: any;
// declare const barbaPrefetch: any;
//const LOG_PREFIX: string = '[BarbaController]';
interface BarbaControllerOptions {
	scrollToTopOnTransition: boolean;
}

interface BarbaPreventCheckResult {
	shouldPrevent: boolean;
	andSuppressEvent: boolean;
}

const DEBUG_HOOKS: boolean = false;

class BarbaController {
	private static lastPerformanceNow: number | null = null;
	private initialised: boolean = false;
	private _transitionActive: boolean = false;
	private fadeTransitionHandler: BarbaTransitionVertWipe = new BarbaTransitionVertWipe();
	private _useTransitions: boolean = true;

	private emitPageEvent(event: PageEvent, data: any = null) {
		const customEvent = new CustomEvent(event, { detail: data });
		document.dispatchEvent(customEvent);
	}

	navigateTo(url: string): void {
		if (this.initialised) {
			barba.go(url);
		} else {
			window.location.href = url;
		}
	}

	set useTransitions(value: boolean) {
		this._useTransitions = value;
	}

	get transitionActive(): boolean {
		return this._transitionActive;
	}

	init(options: BarbaControllerOptions): void {
		// Abort initialisation if there isn't a valid barba-container on the page
		const container = document.querySelector('[data-barba="container"]');
		if (!container) return;

		this.initialised = true;

		const commonTransitionLeaveCallback = (data: ITransitionData) => {
			if (this._useTransitions) {
				return Promise.resolve(this.fadeTransitionHandler.handleLeave(data));
			}
			return Promise.resolve();
		};
		const commonTransitionEnterCallback = (data: any) => {
			const finalise = () => {
				this._transitionActive = false;
				this.emitPageEvent(PageEvent.enterComplete);
			};
			if (this._useTransitions) {
				return this.fadeTransitionHandler.handleEnter(data, () => finalise());
			}
			finalise();
		};
		const commonBeforeEnterCallback = ({ next }: { next: { html: string } }) => {
			const matches = next.html.match(/<body.+?class="([^""]*)"/i);
			document.body.setAttribute('class', (matches && matches.at(1)) ?? '');
			document.body.classList.add('loaded');
		};

		const commonTransitionPreventCallback = (data: any) => {
			let checkResult: BarbaPreventCheckResult;

			checkResult = this.preventCheck_elementContainsBypassClasses(data);
			//logger.log(LOG_PREFIX, 'commonTransitionPreventCallback', '1st check', checkResult);
			if (checkResult.shouldPrevent) {
				if (checkResult.andSuppressEvent) data.event?.preventDefault();
				return true;
			}

			checkResult = this.preventCheck_linkToPDF(data);
			//logger.log(LOG_PREFIX, 'commonTransitionPreventCallback', '2nd check', checkResult);
			if (checkResult.shouldPrevent) {
				if (checkResult.andSuppressEvent) data.event?.preventDefault();
				return true;
			}

			checkResult = this.preventCheck_foundSamePageAnchorNavigation(data);
			//logger.log(LOG_PREFIX, 'commonTransitionPreventCallback', '3rd check', checkResult);
			if (checkResult.shouldPrevent) {
				if (checkResult.andSuppressEvent) data.event?.preventDefault();
				return true;
			}

			// Don't prevent the transition
			return false;
		};

		barba.use(barbaPrefetch, {});

		barba.init({
			cacheFirstPage: false,
			cacheIgnore: true,
			debug: false,
			logLevel: 'error',
			prefetchIgnore: true,
			prevent: (data: any) => commonTransitionPreventCallback(data),
			preventRunning: false,
			timeout: 5000,
			transitions: [
				{
					name: 'default-transition',
					sync: false,
					//to: { route: ['index', 'about'] },
					leave: (data: ITransitionData) => commonTransitionLeaveCallback(data),
					beforeEnter: (data: ITransitionData) => commonBeforeEnterCallback(data),
					enter: (data: ITransitionData) => commonTransitionEnterCallback(data),
				},
				{
					name: 'self',
					sync: false,
					leave: (data: any) => commonTransitionLeaveCallback(data),
					beforeEnter: (data: any) => commonBeforeEnterCallback(data),
					enter: (data: any) => commonTransitionEnterCallback(data),
				},
			],
			views: [],
		});

		// Before
		barba.hooks.before(() => {
			// The browser seems to reset history.scrollRestoration to 'auto' at indeterminate
			// times. This seems to be an effective time to set it back to 'manual'.
			this.tryUpdateScrollRestoration();
		});

		// As the leave transition starts
		barba.hooks.leave((data: any) => {
			this._transitionActive = true;
			this.emitPageEvent(PageEvent.leave, { current: data.current, next: data.next });
		});

		// After the leave transition
		barba.hooks.afterLeave((_data: any) => {
			if (options.scrollToTopOnTransition) {
				// If requested to scroll to top on all page visits, disable scroll smoothing so
				// that the reset can happen instantaneously, and avoid the risk of seeing part
				// of the animated scroll-to-top brought about by ScrollSmoother.
				scrollController.trySetSmoothingEnabled(false);
				document.documentElement.style.scrollBehavior = 'auto';
				window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
			}
		});

		// As the enter transition starts
		barba.hooks.enter((data: any) => {
			this.emitPageEvent(PageEvent.enter, { current: data.current, next: data.next });
			// Re-enable scroll smoothing
			scrollController.trySetSmoothingEnabled(true);
			scrollController.scrollSmoother?.scrollTo(0, false);
		});

		// After the enter transition
		barba.hooks.afterEnter(() => {
			// The browser seems to reset history.scrollRestoration to 'auto' at indeterminate
			// times. This seems to be an effective time to set it back to 'manual'.
			this.tryUpdateScrollRestoration();

			if (window.gtag) {
				window.gtag('event', 'page_view', {
					page_title: document.title,
					page_location: location.href,
					page_path: location.pathname,
				});
			}

		});

		DEBUG_HOOKS && this.addDebugHooks();
	}

	private addDebugHooks(): void {
		barba.hooks.after(() => this.logWithTime('after'));
		barba.hooks.afterEnter(() => this.logWithTime('afterEnter'));
		barba.hooks.afterLeave(() => this.logWithTime('afterLeave'));
		barba.hooks.afterOnce(() => this.logWithTime('afterOnce'));
		barba.hooks.before(() => this.logWithTime('before'));
		barba.hooks.beforeEnter(() => this.logWithTime('beforeEnter'));
		barba.hooks.beforeLeave(() => this.logWithTime('beforeLeave'));
		barba.hooks.beforeOnce(() => this.logWithTime('beforeOnce'));
		barba.hooks.currentAdded(() => this.logWithTime('currentAdded'));
		barba.hooks.currentRemoved(() => this.logWithTime('currentRemoved'));
		barba.hooks.enter(() => this.logWithTime('enter'));
		barba.hooks.leave(() => this.logWithTime('leave'));
		barba.hooks.nextAdded(() => this.logWithTime('nextAdded'));
		barba.hooks.nextRemoved(() => this.logWithTime('nextRemoved'));
		barba.hooks.once(() => this.logWithTime('once'));
		barba.hooks.page(() => this.logWithTime('page'));
		barba.hooks.ready(() => this.logWithTime('ready'));
		barba.hooks.reset(() => this.logWithTime('reset'));
	}

	private tryUpdateScrollRestoration(): void {
		if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
	}

	logWithTime(msg: string, prefix: string = '[Barba hook] '): void {
		const time = (performance.now() / 1000).toFixed(3);
		let timeDifference = '';
		if (BarbaController.lastPerformanceNow) {
			timeDifference = `(${(performance.now() - BarbaController.lastPerformanceNow).toFixed(0)}ms later)`;
		}
		BarbaController.lastPerformanceNow = performance.now();
		console.log(time, `${prefix}${msg}`, timeDifference);
	}

	private preventCheck_foundSamePageAnchorNavigation(data: any): BarbaPreventCheckResult {
		if (data.href) {
			// If the next URL is the same as the current URL, and differs in hash,
			// this should be a same-page anchor navigation, so prevent the transition
			const currentUrl = new URL(window.location.href);
			const nextUrl = new URL(data.href);
			const urlDiffersByHash = currentUrl.pathname === nextUrl.pathname && currentUrl.hash !== nextUrl.hash;
			return { shouldPrevent: urlDiffersByHash, andSuppressEvent: true };
		}
		return { shouldPrevent: false, andSuppressEvent: false };
	}

	private preventCheck_elementContainsBypassClasses(data: any): BarbaPreventCheckResult {
		if (!data.el) return { shouldPrevent: false, andSuppressEvent: false };
		/**
		 * @a11y - Test to see if target element contains particular classes.
		 */
		const { classList } = data.el;
		const elementContainsNoTransitionClass = classList.contains('no-transition');
		const elementContainsScreenReaderTextClass = classList.contains('screen-reader-text');
		const containsClasses = elementContainsNoTransitionClass || elementContainsScreenReaderTextClass;

		// If the element's classlist contains either blocker class then prevent the transition
		return { shouldPrevent: containsClasses, andSuppressEvent: true };
	}

	private preventCheck_linkToPDF(data: any): BarbaPreventCheckResult {
		if (data.href) {
			const url = new URL(data.href, window.location.href);
			const isLinkToPDF = url.pathname.toLowerCase().endsWith('.pdf');
			return { shouldPrevent: isLinkToPDF, andSuppressEvent: true };
		}
		return { shouldPrevent: false, andSuppressEvent: false };
	}

}

export const barbaController = new BarbaController();
